import type { Project } from '../db/models'

/*
 * **Unterprojekte als Baum** (#185). Reine Rechnung auf den lebenden Projekten EINES
 * Lebensbereichs, die eine Quelle hereinreicht — kein DOM, keine Datenbank.
 *
 * `order` zählt über den ganzen Lebensbereich in der Reihenfolge des aufgeklappten Baums
 * (ADR 0033): Elternprojekt, dann seine Kinder, dann das nächste Geschwister. Eine ältere
 * App-Version, die `parentId` nicht kennt, zeigt die Kinder so flach, aber am richtigen Platz.
 *
 * Kaputte Daten (ein Zyklus aus dem Sync, ein Elternprojekt in einem anderen Lebensbereich
 * oder gelöscht) stellen das Projekt nach oben, statt es verschwinden zu lassen.
 */

/** Höchstens so viele Ebenen: Projekt → Unterprojekt → … → Ebene 4, wie bei Unteraufgaben. */
export const MAX_PROJECT_DEPTH = 4

/** Einrückung je Ebene in der Sidebar — auch der waagrechte Weg für eine Ebene beim Ziehen. */
export const PROJECT_INDENT_PX = 24

export type TreeProject = Pick<Project, 'id' | 'order'> & { parentId?: string | null }

export interface ProjectTreeRow<T extends TreeProject = TreeProject> {
  project: T
  id: string
  /** 0 = oben im Lebensbereich, `MAX_PROJECT_DEPTH - 1` = Ebene 4. */
  depth: number
  /** Das Elternprojekt, wie der Baum es sieht — `null`, wenn `parentId` ins Leere zeigt. */
  parentId: string | null
  hasChildren: boolean
  /** Liegt in einem zugeklappten Ast. */
  hidden: boolean
}

/**
 * Der aufgeklappte Baum: jede Zeile mit Tiefe, in Anzeige-Reihenfolge. Mit `collapsed` sind
 * die Zeilen unter einem zugeklappten Projekt `hidden` — sie bleiben in der Liste, damit die
 * Reihenfolge für `order` vollständig ist.
 */
export function flattenProjectTree<T extends TreeProject>(
  projects: readonly T[],
  collapsed?: ReadonlySet<string>,
): ProjectTreeRow<T>[] {
  const ids = new Set(projects.map(p => p.id))
  const kids = new Map<string | null, T[]>()
  for (const p of projects) {
    const key = p.parentId && p.parentId !== p.id && ids.has(p.parentId) ? p.parentId : null
    const list = kids.get(key)
    if (list) list.push(p)
    else kids.set(key, [p])
  }
  for (const list of kids.values()) list.sort((a, b) => a.order - b.order)

  const rows: ProjectTreeRow<T>[] = []
  const seen = new Set<string>()
  const walk = (parentId: string | null, depth: number, hidden: boolean) => {
    for (const p of kids.get(parentId) ?? []) {
      if (seen.has(p.id)) continue
      seen.add(p.id)
      rows.push({ project: p, id: p.id, depth, parentId, hasChildren: (kids.get(p.id)?.length ?? 0) > 0, hidden })
      walk(p.id, depth + 1, hidden || (collapsed?.has(p.id) ?? false))
    }
  }
  walk(null, 0, false)
  // Ein Zyklus hängt an keiner Wurzel: sein erstes Glied kommt nach oben, der Rest darunter.
  for (const p of [...projects].sort((a, b) => a.order - b.order)) {
    if (seen.has(p.id)) continue
    seen.add(p.id)
    rows.push({ project: p, id: p.id, depth: 0, parentId: null, hasChildren: (kids.get(p.id)?.length ?? 0) > 0, hidden: false })
    walk(p.id, 1, collapsed?.has(p.id) ?? false)
  }
  return rows
}

/** Die Unterprojekte unter `id`, beliebig tief, in Anzeige-Reihenfolge. */
export function projectDescendantIds(projects: readonly TreeProject[], id: string): string[] {
  const rows = flattenProjectTree(projects)
  const i = rows.findIndex(r => r.id === id)
  if (i < 0) return []
  const out: string[] = []
  for (let j = i + 1; j < rows.length && rows[j].depth > rows[i].depth; j++) out.push(rows[j].id)
  return out
}

/** Die Elternprojekte über `id`, vom nächsten hinauf. */
export function projectAncestorIds(projects: readonly TreeProject[], id: string): string[] {
  const byId = new Map(flattenProjectTree(projects).map(r => [r.id, r]))
  const out: string[] = []
  for (let parent = byId.get(id)?.parentId ?? null; parent && !out.includes(parent); parent = byId.get(parent)?.parentId ?? null) {
    out.push(parent)
  }
  return out
}

/** 0 = oben im Lebensbereich. Ein unbekanntes Projekt gilt als oben. */
export function projectDepth(projects: readonly TreeProject[], id: string): number {
  return flattenProjectTree(projects).find(r => r.id === id)?.depth ?? 0
}

/** Wie viele Ebenen der Ast hat: 1 = nur das Projekt selbst. */
export function projectSubtreeHeight(projects: readonly TreeProject[], id: string): number {
  const rows = flattenProjectTree(projects)
  const i = rows.findIndex(r => r.id === id)
  if (i < 0) return 1
  let height = 1
  for (let j = i + 1; j < rows.length && rows[j].depth > rows[i].depth; j++) {
    height = Math.max(height, rows[j].depth - rows[i].depth + 1)
  }
  return height
}

/** Ob unter `id` noch ein Unterprojekt Platz hat (nicht auf Ebene 4). */
export function canHoldSubproject(projects: readonly TreeProject[], id: string): boolean {
  return projectDepth(projects, id) + 1 < MAX_PROJECT_DEPTH
}

export interface DropProjectionInput {
  /** Die sichtbaren Zeilen OHNE den gezogenen Ast. */
  rest: readonly Pick<ProjectTreeRow, 'id' | 'depth'>[]
  /** Die Stelle in `rest`, vor der der Ast landet (`rest.length` = ganz unten). */
  insertAt: number
  /** Tiefe des gezogenen Projekts beim Anfassen. */
  startDepth: number
  /** Waagrechter Weg in Einrückungen (gerundet, negativ = nach links). */
  levels: number
  /** {@link projectSubtreeHeight} des gezogenen Asts. */
  height: number
}

export type DropProjection =
  | { ok: true; depth: number; parentId: string | null; afterId: string | null }
  /** Kein Platz: Der Ast ragte über Ebene 4. `depth` sagt nur, wo die graue Linie steht. */
  | { ok: false; depth: number }

/**
 * **Wohin ein gezogener Ast fällt** (Todoist-Stil): hoch und runter wählt die Stelle, links und
 * rechts die Ebene. Die Tiefe ist die Starttiefe plus der waagrechte Weg, begrenzt
 * - nach oben: höchstens die Zeile darüber + 1, und der Ast samt Kindern nicht über Ebene 4;
 * - nach unten: mindestens die Zeile darunter — sonst verlöre sie ihr Elternprojekt.
 * Passt beides nicht zusammen, ist dort kein Platz.
 */
export function projectDropProjection({ rest, insertAt, startDepth, levels, height }: DropProjectionInput): DropProjection {
  const prev = rest[insertAt - 1]
  const next = rest[insertAt]
  const maxDepth = Math.min(prev ? prev.depth + 1 : 0, MAX_PROJECT_DEPTH - height)
  const minDepth = next ? next.depth : 0
  if (minDepth > maxDepth) return { ok: false, depth: Math.max(0, maxDepth) }
  const depth = Math.max(minDepth, Math.min(maxDepth, startDepth + levels))
  let parentId: string | null = null
  let afterId: string | null = null
  for (let i = insertAt - 1; i >= 0; i--) {
    const row = rest[i]
    if (row.depth < depth) { parentId = row.id; break }
    if (row.depth === depth && afterId === null) afterId = row.id
  }
  return { ok: true, depth, parentId, afterId }
}

/** Eine sichtbare Zeile der Sidebar, beim Anfassen gemessen (oben/unten in px). */
export interface MeasuredProjectRow {
  id: string
  depth: number
  top: number
  bottom: number
}

export interface ProjectDropTarget {
  projection: DropProjection
  /** Die Fuge, in der die Ablage-Linie steht — in den Koordinaten der Zeilen. */
  lineY: number
}

/**
 * **Vom Zeiger zur Ablage** (Sidebar, #185): Welche Fuge die Karte am Zeiger trifft und in
 * welcher Tiefe der Ast dort landet. Die Zeilen bewegen sich beim Ziehen nicht — der gezogene
 * Ast bleibt blass an seinem Platz, und jede Fuge in ihm heißt „bleibt, wo es ist“.
 *
 * `centerY` ist die Mitte der Karte, `offsetX` der waagrechte Weg seit dem Anfassen.
 */
export function projectDropTarget({ rows, draggedId, centerY, offsetX, height }: {
  rows: readonly MeasuredProjectRow[]
  draggedId: string
  centerY: number
  offsetX: number
  height: number
}): ProjectDropTarget | null {
  const start = rows.findIndex(r => r.id === draggedId)
  if (start < 0) return null
  let end = start
  while (end + 1 < rows.length && rows[end + 1].depth > rows[start].depth) end++

  const gap = rows.filter(r => (r.top + r.bottom) / 2 < centerY).length
  let insertAt = gap
  let lineGap = gap
  if (gap > start && gap <= end + 1) {
    insertAt = start
    if (gap <= end) lineGap = start
  } else if (gap > end + 1) {
    insertAt = gap - (end - start + 1)
  }

  const rest = [...rows.slice(0, start), ...rows.slice(end + 1)]
  const projection = projectDropProjection({
    rest,
    insertAt,
    startDepth: rows[start].depth,
    levels: Math.round(offsetX / PROJECT_INDENT_PX),
    height,
  })
  const lineY = lineGap === 0
    ? rows[0].top
    : lineGap === rows.length
      ? rows[rows.length - 1].bottom
      : (rows[lineGap - 1].bottom + rows[lineGap].top) / 2
  return { projection, lineY }
}

export interface ProjectTreeChange {
  id: string
  order: number
  parentId: string | null
}

/** Neue `order` (0, 1, 2 …) in Baum-Reihenfolge — nur die Zeilen, die sich ändern. */
export function renumberProjectTree(projects: readonly TreeProject[]): ProjectTreeChange[] {
  return changesFor(projects, flattenProjectTree(projects).map(r => ({ id: r.id, parentId: r.parentId })))
}

/**
 * Hängt `id` samt Ast unter `parentId`, direkt hinter das Geschwister `afterId` (`null` = als
 * erstes Kind), und nummeriert den Lebensbereich neu. Gibt nur geänderte Zeilen aus; leer, wenn
 * der Zug nicht geht (Elternprojekt im eigenen Ast oder unbekannt).
 */
export function applyProjectMove(
  projects: readonly TreeProject[],
  id: string,
  parentId: string | null,
  afterId: string | null,
): ProjectTreeChange[] {
  const rows = flattenProjectTree(projects)
  if (!rows.some(r => r.id === id)) return []
  const branch = new Set([id, ...projectDescendantIds(projects, id)])
  if (parentId !== null && (branch.has(parentId) || !rows.some(r => r.id === parentId))) return []

  const kids = new Map<string | null, string[]>()
  for (const r of rows) {
    if (r.id === id) continue
    const list = kids.get(r.parentId)
    if (list) list.push(r.id)
    else kids.set(r.parentId, [r.id])
  }
  const siblings = kids.get(parentId) ?? []
  const at = afterId ? siblings.indexOf(afterId) + 1 : 0
  siblings.splice(at, 0, id)
  kids.set(parentId, siblings)

  const sequence: { id: string; parentId: string | null }[] = []
  const walk = (parent: string | null) => {
    for (const child of kids.get(parent) ?? []) {
      sequence.push({ id: child, parentId: parent })
      walk(child)
    }
  }
  walk(null)
  return changesFor(projects, sequence)
}

function changesFor(projects: readonly TreeProject[], sequence: readonly { id: string; parentId: string | null }[]): ProjectTreeChange[] {
  const byId = new Map(projects.map(p => [p.id, p]))
  const changes: ProjectTreeChange[] = []
  sequence.forEach(({ id, parentId }, order) => {
    const p = byId.get(id)
    if (!p) return
    if (p.order === order && (p.parentId ?? null) === parentId) return
    changes.push({ id, order, parentId })
  })
  return changes
}
